// Sticky, keyboard-navigable tab bar for the right control dock.
// Uses the WAI-ARIA tabs pattern (tablist / tab / tabpanel) with automatic
// activation: arrow keys move the selection and focus together.
//
// The tab bar lives INSIDE the scrolling dock, so `position: sticky` keeps it
// visible while the active tab's content scrolls underneath.

import React, { useRef } from 'react';

export type PanelTab = 'separations' | 'colorways' | 'repeat';

export const PANEL_TABS: { id: PanelTab; label: string }[] = [
  { id: 'separations', label: 'Separations' },
  { id: 'colorways', label: 'Colorways' },
  { id: 'repeat', label: 'Repeat' },
];

interface RightPanelProps {
  active: PanelTab;
  onChange: (tab: PanelTab) => void;
  /** All three panels are mounted at once (hidden when inactive) so local UI
   *  state — e.g. the Auto Variants theory/seed — survives tab switches. */
  panels: Record<PanelTab, React.ReactNode>;
}

export default function RightPanel({ active, onChange, panels }: RightPanelProps) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const focusAndSelect = (tab: PanelTab) => {
    onChange(tab);
    // Focus after React has applied the state (same tick is enough here).
    tabRefs.current[tab]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const index = PANEL_TABS.findIndex((t) => t.id === active);
    if (index < 0) return;
    let next = index;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % PANEL_TABS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + PANEL_TABS.length) % PANEL_TABS.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = PANEL_TABS.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    focusAndSelect(PANEL_TABS[next].id);
  };

  return (
    <div className="right-panel">
      <div
        className="right-tabs"
        role="tablist"
        aria-label="Control panel"
        onKeyDown={handleKeyDown}
      >
        {PANEL_TABS.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              type="button"
              role="tab"
              id={`panel-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`panel-tabpanel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={`right-tab${selected ? ' active' : ''}`}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {PANEL_TABS.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`panel-tabpanel-${tab.id}`}
          aria-labelledby={`panel-tab-${tab.id}`}
          hidden={tab.id !== active}
          className="right-tabpanel"
        >
          {panels[tab.id]}
        </div>
      ))}
    </div>
  );
}
